import React, {useCallback, useRef, useState} from 'react';
import APIServicesRegistry from '../../api-services/APIServicesRegistry';
import UploadAPIService from '../../api-services/UploadApiService';
import useForceUpdate from '../../hooks/useForceUpdate';
import useObserve from '../../hooks/useObserve';
import ProjectStore from '../../store/projectStore';
import Button from '../common/Button';
import Loader from '../common/Loader';
import Card from '../edit-page/Card';
import IconDelete from '../icons/IconDelete';
import Warning from '../../components/common/Warning';
import {parse} from 'content-disposition';
import IconEdit from '../icons/IconEdit';
import IconAccept from '../icons/IconAccept';

export default function ProjectFilesCard(props) {
    useObserve(ProjectStore);

    const [duringEdit, setDuringEdit] = useState([])
    const editable = ProjectStore.filesDuringEdit;
    const [itemsViewModel, setItemsViewModel] = useState(props.initialItemsViewModel);
    const [overDropArea, setOverDropArea] = useState(false);
    const [files, setFiles] = useState(props.initialFiles || []);
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isCloseFilesTable, setIsCloseFilesTable] = useState(false);
    const forceUpdate = useForceUpdate();
    const hiddenFileInputRef = useRef(null);
    const areFilesBeingUploaded = filesToUpload.length > 0;

    const addFiles = (filesToAdd) => {
        if (filesToAdd.length === 0) {
            return;
        }

        setFilesToUpload(filesToAdd);
        setUploadProgress(0);

        const errorCatch = e => {
            console.error(e);
            setFilesToUpload([]);
            setUploadProgress(0);
        }

        var upload = APIServicesRegistry.getService(UploadAPIService).upload(filesToAdd, handleUploadFilesProgress)
        upload.then(uploadResponse => {
            var collection = APIServicesRegistry.getService(props.documentApiService).getFileCollection({
                id: props.id,
                fileIds: uploadResponse.data.id
            })
            //var collection = props.documentApiService.getFileCollection(uploadResponse.data.id)
            collection.then(collectionReponse => {
                setItemsViewModel([...itemsViewModel, ...collectionReponse.data.itemsViewModel])
                const newFiles = [...files, ...collectionReponse.data.items];
                setFiles(newFiles);
                props.setDirty();
                setFilesToUpload([]);
                setUploadProgress(0);
                props.onFilesChange(newFiles);
            }).then(e => errorCatch(e))
        }).then(e => errorCatch(e))
    }


    const handleToggleEditable = useCallback((id) => {
        var newIds = _.xor(duringEdit, [id]);
        setDuringEdit(newIds);
        props.onFilesChange(files);
        // if (props.duringEdit) {
        //     if (typeof props.onFilesChange === 'function') {
        //         props.onFilesChange(files);
        //     }
        // }

        props.setDuringEdit(newIds.length > 0)
    });

    const handleUploadFilesProgress = useCallback((progress) => {
        setUploadProgress((progress.loaded / progress.total) * 100);
    });

    const handleDropAreaClick = useCallback(e => {
        if (areFilesBeingUploaded) return;

        e.preventDefault();

        if (hiddenFileInputRef.current) {
            hiddenFileInputRef.current.click();
        }
    }, [areFilesBeingUploaded]);

    const handleDropAreaDragEnter = useCallback(e => {
        if (areFilesBeingUploaded) return;
        e.preventDefault();
        setOverDropArea(true);
    }, [areFilesBeingUploaded]);

    const handleDropAreaDragLeave = useCallback(e => {
        if (areFilesBeingUploaded) return;
        setOverDropArea(false);
    }, [areFilesBeingUploaded]);

    const handleDropAreaDragOver = useCallback(e => {
        if (areFilesBeingUploaded) return;
        e.preventDefault();
    }, [areFilesBeingUploaded]);

    const handleDropAreaDrop = useCallback(e => {
        if (areFilesBeingUploaded) return;
        e.preventDefault();
        setOverDropArea(false);

        addFiles(e.dataTransfer.files);
    });

    const handleHiddenFileInputChange = useCallback(e => {
        addFiles(e.target.files);
    });

    const handleRemoveButtonClick = useCallback(file => {
        let newFiles = [...files];
        let index = newFiles.findIndex(x => x.fileId === file.fileId);

        let newItemsViewModel = [...itemsViewModel];
        newItemsViewModel.splice(index, 1);
        setItemsViewModel(newItemsViewModel);

        newFiles.splice(index, 1);
        setFiles(newFiles);

        props.setDirty();
        props.onFilesChange(newFiles);
    }, [files]);

    const downloadClick = (file) => {
        if (!file.downloadUrl) {
            return;
        }

        const response = {
            file: file.downloadUrl,
        };

        window.open(response.file);
    }

    const createFileRoleDropdown = (file, index) => {
        var itemViewModel = itemsViewModel[index];
        var fileRoles = (itemViewModel['fileRole'] || itemViewModel['FileRole']).items; //serialize not working correct

        if (duringEdit.includes(file.fileId) && fileRoles.length > 1 && file.canEdit) {
            return (
                <select value={file.fileRole} onChange={e => {
                    file.fileRole = e.target.value;
                    forceUpdate();
                    props.setDirty()
                }}>
                    {fileRoles.map(o => (
                        <option key={o.value} value={o.value}>{o.text}</option>
                    ))}
                </select>
            )

        }

        var fileRole = fileRoles.find(x => x.value == file.fileRole)
        var isReplacement = fileRole.attributes.isReplacement.toLowerCase() === 'true';

        return (
            <React.Fragment>
                <span>{(fileRole || {}).text}</span>
                {isReplacement &&
                    <span>{Warning.CreateWarning(__('Replaces original document in document generator!'), 'orange')}</span>}
            </React.Fragment>
        )
    }

    const createDescriptionInput = file => {
        if (duringEdit.includes(file.fileId) && file.canEdit) {
            return (
                <input type="text"
                       style={{minWidth: '290px'}}
                       value={file.description || ''}
                       onChange={e => {
                           file.description = e.target.value;
                           forceUpdate();
                           props.setDirty()
                       }}
                />
            )
        }

        return file.description;
    }

    return (
        <Card helpInfo={props.helpInfo} editableMode={props.allowEdit} editable={props.duringEdit} label="Files"
              isCloseFilesTable={isCloseFilesTable} setIsCloseFilesTable={setIsCloseFilesTable}>
            {props.allowEdit && (
                <React.Fragment>
                    <div
                        className={`project-files-drop-area ${overDropArea ? 'project-files-drop-area--over' : ''} ${isCloseFilesTable ? "close" : ""}`}
                        onClick={handleDropAreaClick}
                        onDragEnter={handleDropAreaDragEnter}
                        onDragLeave={handleDropAreaDragLeave}
                        onDragOver={handleDropAreaDragOver}
                        onDrop={handleDropAreaDrop}
                    >
                        {areFilesBeingUploaded ? (
                            <React.Fragment>
                                <Loader fillContainer={true}/>
                                <div className="project-files-drop-area-progress"
                                     style={{width: uploadProgress + '%'}}></div>
                            </React.Fragment>
                        ) : (
                            __('Drag and drop files or click to browse from disk.')
                        )}
                    </div>

                    <input type="file" multiple={true} ref={hiddenFileInputRef} onChange={handleHiddenFileInputChange}
                           hidden={true}/>
                </React.Fragment>
            )}

            {files.length === 0 ? (
                <div className="text-muted">{__('No files have been added to this project, yet.')}</div>
            ) : (
                <div className={`project-files-container ${isCloseFilesTable ? "close" : ""}`}>
                    {files.map((file, index) => (
                        <React.Fragment key={file.id}>
                            <div className="project-file-container">
                                <div className="project-file-icons">
                                    {duringEdit.includes(file.fileId) && (
                                        <Button theme={Button.Theme.TextOnly} size={Button.Size.Tiny}
                                                onClick={() => handleToggleEditable(file.fileId)}>
                                            <IconAccept/>
                                        </Button>
                                    )}
                                    {duringEdit.includes(file.fileId) === false && (
                                        <Button theme={Button.Theme.TextOnly} size={Button.Size.Tiny}
                                                onClick={() => handleToggleEditable(file.fileId)}>
                                            <IconEdit/>
                                        </Button>
                                    )}
                                    <Button theme={Button.Theme.TextOnly} size={Button.Size.Tiny}
                                            onClick={() => handleRemoveButtonClick(file)}>
                                        <IconDelete size={IconDelete.Size.Small}/>
                                    </Button>
                                </div>
                                <div className="small-table responsive-table-container project-files-table">
                                    <table>
                                        <thead>
                                        <tr>
                                            <th></th>
                                            <th>{__('File name')}</th>
                                            <th>{__('Author')}</th>
                                            <th>{__('Date')}</th>
                                        </tr>
                                        </thead>

                                        <tbody>
                                        <React.Fragment>
                                            <tr className={duringEdit.includes(file.fileId) ? "main main-editable" : "main"}>
                                                <td>
                                                    {file.thumbnail && (
                                                        <div className="project-files-thumbnail">
                                                            <img src={file.thumbnail}
                                                                 onClick={() => downloadClick(file)}/>
                                                        </div>
                                                    )}
                                                </td>
                                                <td><span className="link"
                                                          onClick={() => downloadClick(file)}>{file.name}</span></td>
                                                <td>{file.author}</td>
                                                <td>{file.date}</td>
                                                {/* <td>
    
                                                    </td> */}
                                            </tr>
                                            <tr className={duringEdit.includes(file.fileId) ? "meta meta-editable" : "meta"}>
                                                <td></td>
                                                <td colSpan="4">
                                                    <table>
                                                        <tbody>
                                                        <tr>
                                                            <td>
                                                                <div className="label">{__('File role')}</div>
                                                            </td>
                                                            <td colSpan="3"><span
                                                                className="file-role-name">{createFileRoleDropdown(file, index)}</span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>
                                                                <div className="label">{__('Description')}</div>
                                                            </td>
                                                            <td><span>{createDescriptionInput(file)}</span></td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </td>

                                            </tr>
                                        </React.Fragment>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </React.Fragment>
                    ))}

                </div>
            )}
        </Card>
    )
}